import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import { useTrans, useFindMenu, useTealium } from '@next-nx/hooks'
import { getTimestamp } from '@next-nx/utils'
import { ArrowRight, NavigationCancel, Search } from '@next-nx/shared-ui/icons'
import { WContainer, Button, Chip, FormInput, Loader } from '@next-nx/shared-ui/atoms'
import { OffertaCardSearch } from '@next-nx/shared-ui/molecules'
import { HeaderSimple, ProdottiSearch } from '@next-nx/shared-ui/organisms'
import styles from './SearchOverlay.module.sass'
import {
  SearchQuery,
  useOfferteQuery,
  useSearchLazyQuery,
} from '@next-nx/shared-graphql/unipolrental'
import {
  getMappingResult,
  LIMIT_AUTOCOMPLETE_RESULTS,
  LIMIT_SEARCH_RESULTS,
  MODELLI_SEARCH,
  MODELLO_SEARCH_ARTICOLI,
  MODELLO_SEARCH_CATEGORIE,
  MODELLO_SEARCH_FAQ,
  MODELLO_SEARCH_OFFERTA,
  MODELLO_SEARCH_PAGINE,
  NUM_CHARS_PER_SEARCH,
} from '@next-nx/utils/search'

interface Props {
  visible: boolean
  setVisible: (v: boolean) => void
  className?: string
}

const SearchOverlay = (props: Props) => {
  const { visible = false, setVisible = undefined, className = '' } = props

  const t = useTrans()

  const topSearchMenu = useFindMenu('top-search')
  const linksMenu = useFindMenu('links', topSearchMenu)
  const offerteMenu = useFindMenu('prodotti', topSearchMenu)
  const { linkDataLayer } = useTealium()

  const [inputValue, setInputValue] = useState('')
  const [searchData, setSearchData] = useState<SearchQuery | undefined>()
  const [refetch, { data, loading }] = useSearchLazyQuery()
  const [isFetched, setFetched] = useState(false)
  const [searchEnabled, setSearchEnalbled] = useState(false)

  const { data: offerteData } = useOfferteQuery({
    variables: {
      promoWeb: true,
      first: 2,
    },
  })
  const offerteInEvidenza =
    (offerteData?.offerte && offerteData.offerte.edges.map((x) => x?.node)) || []

  const handleFetch = (searchValue: string) => {
    refetch({
      variables: {
        query: searchValue,
        models: MODELLI_SEARCH,
        limitAutocomplete: LIMIT_AUTOCOMPLETE_RESULTS,
      },
    })

    setFetched(true)
  }

  const handleTracking = (action_type = '', research_type = inputValue) => {
    const action_timestamp = getTimestamp()

    linkDataLayer({
      virtual: 'action',
      action_name: 'ricerca',
      action_tech: 'click',
      action_type: action_type.replace(/ /g, '_').toLowerCase(),
      action_timestamp,
      ...(action_type === 'chiudi' || action_type === 'cancella'
        ? { action_effect: 'chiusura_ricerca' }
        : { action_effect: 'visualizzazione_risultati_ricerca' }),
      research_type: research_type
        .replace(/\/|\s+/g, '_')
        .replace(/\./g, '')
        .toLowerCase(),
    })
  }

  useEffect(() => {
    if (!inputValue) {
      setSearchData(undefined)
      setFetched(false)
    }

    // Se l'input è stringa vuota => ricerca disabilitata
    // All'inizio bisogna inserire almeno 3 caratteri per abilitare la ricerca
    // successivamente anche con meno di 3 caratteri la ricerca è abilitata
    const enabled = !inputValue
      ? false
      : !searchEnabled
      ? inputValue?.toString()?.split('')?.length >= NUM_CHARS_PER_SEARCH
      : searchEnabled
    setSearchEnalbled(enabled)

    const timeout = setTimeout(() => {
      inputValue && enabled && handleFetch(inputValue)
    }, 400)

    // ! Rimuove il timeout precedente nel caso dovesse essere ritriggerato
    return () => clearTimeout(timeout)
  }, [inputValue])

  useEffect(() => {
    setSearchData(data)
  }, [data])

  const handleOnSubmit = (e) => {
    if (!inputValue?.length) {
      e.preventDefault()
    }
    handleTracking('diretta')
  }

  const { offerte, categorie, articoli, pagine, faq, suggestion, autocomplete } =
    getMappingResult(searchData)

  const fetched = Boolean(
    isFetched ||
      offerte.length ||
      categorie.length ||
      articoli.length ||
      pagine.length ||
      faq.length ||
      suggestion ||
      autocomplete.length
  )

  return visible ? (
    <div
      className={`${styles.searchOverlay} ${fetched ? styles['searchOverlay--fetched'] : ''} ${
        className ? ` ${className}` : ''
      }`}
    >
      {!!setVisible && (
        <HeaderSimple setSearchActive={setVisible} handleTracking={handleTracking} />
      )}
      <div className={styles.formWrap}>
        <WContainer variant="xs">
          <p className={styles.title}>{t('Cosa stai cercando?')}</p>
          <form className={styles.form} action="/ricerca" onSubmit={handleOnSubmit}>
            <div className={styles.inputWrap}>
              <FormInput
                autoFocus
                name="q"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={t('Cerca per prodotto, marchio...')}
              />
              {!!inputValue && (
                <button
                  type="button"
                  className={styles.inputReset}
                  onClick={() => {
                    setInputValue('')
                    handleTracking('cancella', '-')
                  }}
                >
                  <NavigationCancel />
                </button>
              )}
              <button className={styles.submit} type="submit">
                <Search />
              </button>
            </div>
          </form>
          {!fetched ? (
            <div className={styles.links}>
              {linksMenu &&
                linksMenu.children?.edges &&
                linksMenu.children.edges
                  .slice(0, 4)
                  .map((item) => (
                    <Chip
                      variant="lg"
                      label={item.node.name || ''}
                      url={item.node.url ? item.node.url : '/'}
                      key={item.node.pk}
                      handleTracking={() => handleTracking('shortcut', item.node.name ?? '')}
                    />
                  ))}
            </div>
          ) : offerte.length ||
            categorie.length ||
            articoli.length ||
            pagine.length ||
            faq.length ? (
            <div className={styles.resultsContainer}>
              {offerte.length > 0 && (
                <div className={`${styles.resultsContent} ${styles['resultsContent--full']}`}>
                  <p className={styles.subTitle}>{t('Prodotti')}</p>
                  <div className={styles.items}>
                    {offerte.slice(0, LIMIT_SEARCH_RESULTS).map((x: any, index) => (
                      <OffertaCardSearch
                        key={index}
                        titolo={x?.titolo}
                        url={x?.url || ''}
                        /* image={
                          'https://www.unipolrental.it/_next/image?url=https%3A%2F%2Fwww.unipolrental.it%2Fmedia%2Ffiler_public%2F0b%2Fe2%2F0be260d5-119e-4555-8108-812879fa202e%2Fcategoria_suv.png&w=750&q=75'
                        } */
                        image={x?.veicolo?.mainImmagineStatica || ''}
                        onClick={() => handleTracking('prodotti', x?.titolo)}
                      />
                    ))}
                  </div>
                  {offerte.length > LIMIT_SEARCH_RESULTS && (
                    <div className={styles.ctaBox}>
                      <Button
                        href={`/ricerca?q=${inputValue}&option=${MODELLO_SEARCH_OFFERTA}`}
                        label={t('Vedi {0} risultati per "{1}"', [
                          offerte.length.toString(),
                          inputValue,
                        ])}
                        variant="ghost"
                        icon={<ArrowRight />}
                        iconPos="right"
                        onClick={() =>
                          handleTracking(
                            'prodotti',
                            t('Vedi {0} risultati per "{1}"', [
                              offerte.length.toString(),
                              inputValue,
                            ])
                          )
                        }
                      />
                    </div>
                  )}
                </div>
              )}
              <div>
                {categorie.length > 0 && (
                  <div className={styles.resultsContent}>
                    <p className={styles.subTitle}>{t('Categorie')}</p>
                    <div className={styles.items}>
                      {categorie.slice(0, LIMIT_SEARCH_RESULTS).map((x, index) => (
                        <div key={index}>
                          <Link href={x?.url || ''}>
                            <a
                              onClick={() =>
                                handleTracking('categorie', x.titoloCategoriaPrimaria ?? x.titolo)
                              }
                            >
                              {x.titoloCategoriaPrimaria ? (
                                <>
                                  <span className={styles.categoriaTitle}>{x.titolo}</span>{' '}
                                  {t('in')} {x.titoloCategoriaPrimaria}
                                </>
                              ) : (
                                x.titolo
                              )}
                            </a>
                          </Link>
                        </div>
                      ))}
                    </div>
                    {categorie.length > LIMIT_SEARCH_RESULTS && (
                      <div className={styles.ctaBox}>
                        <Button
                          href={`/ricerca?q=${inputValue}&option=${MODELLO_SEARCH_CATEGORIE}`}
                          label={t('Vai a tutte le categorie')}
                          variant="ghost"
                          icon={<ArrowRight />}
                          iconPos="right"
                          onClick={() => handleTracking('categorie', 'Vai a tutte le categorie')}
                        />
                      </div>
                    )}
                  </div>
                )}
                {articoli.length > 0 && (
                  <div className={styles.resultsContent}>
                    <p className={styles.subTitle}>{t('Articoli')}</p>
                    <div className={styles.items}>
                      {articoli.slice(0, LIMIT_SEARCH_RESULTS).map((x, index) => (
                        <div key={index}>
                          <Link href={x?.url || ''}>
                            <a onClick={() => handleTracking('articoli', x?.titolo)}>{x?.titolo}</a>
                          </Link>
                        </div>
                      ))}
                    </div>
                    {articoli.length > LIMIT_SEARCH_RESULTS && (
                      <div className={styles.ctaBox}>
                        <Button
                          href={`/ricerca?q=${inputValue}&option=${MODELLO_SEARCH_ARTICOLI}`}
                          label={t('Vai a tutti gli articoli')}
                          variant="ghost"
                          icon={<ArrowRight />}
                          iconPos="right"
                          onClick={() => handleTracking('articoli', 'Vai a tutti gli articoli')}
                        />
                      </div>
                    )}
                  </div>
                )}
                {pagine.length > 0 && (
                  <div className={styles.resultsContent}>
                    <p className={styles.subTitle}>{t('Pagine')}</p>
                    <div className={styles.items}>
                      {pagine.slice(0, LIMIT_SEARCH_RESULTS).map((x, index) => (
                        <div key={index}>
                          <Link href={x?.url || ''}>
                            <a onClick={() => handleTracking('pagine', x?.titolo)}>{x?.titolo}</a>
                          </Link>
                        </div>
                      ))}
                    </div>
                    {pagine.length > LIMIT_SEARCH_RESULTS && (
                      <div className={styles.ctaBox}>
                        <Button
                          href={`/ricerca?q=${inputValue}&option=${MODELLO_SEARCH_PAGINE}`}
                          label={t('Vai a tutte le pagine')}
                          variant="ghost"
                          icon={<ArrowRight />}
                          iconPos="right"
                          onClick={() => handleTracking('pagine', 'Vai a tutte le pagine')}
                        />
                      </div>
                    )}
                  </div>
                )}
                {faq.length > 0 && (
                  <div className={styles.resultsContent}>
                    <p className={styles.subTitle}>{t('FAQ')}</p>
                    <div className={styles.items}>
                      {faq.slice(0, LIMIT_SEARCH_RESULTS).map((x, index) => (
                        <div key={index}>
                          <Link href={x?.url || ''}>
                            <a onClick={() => handleTracking('faq', x?.titolo)}>{x?.titolo}</a>
                          </Link>
                        </div>
                      ))}
                    </div>
                    {faq.length > LIMIT_SEARCH_RESULTS && (
                      <div className={styles.ctaBox}>
                        <Button
                          href={`/ricerca?q=${inputValue}&option=${MODELLO_SEARCH_FAQ}`}
                          label={t('Vai a tutte le FAQ')}
                          variant="ghost"
                          icon={<ArrowRight />}
                          iconPos="right"
                          onClick={() => handleTracking('faq', 'Vai a tutte le faq')}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : suggestion || autocomplete.length > 0 ? (
            <>
              {suggestion && (
                <div className={styles.suggestion}>
                  <p>
                    {t('Forse stai cercando')}{' '}
                    <a
                      className={styles.suggestionLink}
                      onClick={() => {
                        setInputValue(suggestion)
                        handleTracking('suggerimenti', suggestion)
                      }}
                    >
                      {suggestion}
                    </a>
                    ?
                  </p>
                </div>
              )}
              {autocomplete.length > 0 && (
                <div className={styles.autocomplete}>
                  <p className={styles.autocompleteTitle}>{t('I nostri suggerimenti')}</p>
                  <div className={styles.autocompleteResults}>
                    {autocomplete.slice(0, LIMIT_AUTOCOMPLETE_RESULTS).map((x, index) => (
                      <div key={index}>
                        <Link href={x?.url || ''}>
                          <a onClick={() => handleTracking('suggerimenti', x?.titolo)}>
                            {x?.titolo}
                          </a>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : !loading ? (
            <div>
              <p className={styles.noResults}>{t('Non ci sono risultati per questa ricerca')}</p>
            </div>
          ) : (
            <div className={styles.loader}>
              <Loader />
            </div>
          )}
        </WContainer>
      </div>
      {!fetched && <ProdottiSearch item={offerteMenu} offerte={offerteInEvidenza} />}
    </div>
  ) : (
    <></>
  )
}

export default SearchOverlay
